import React, { useState } from 'react';
type DeviceVariant = 'browser' | 'laptop' | 'phone';
interface DeviceFrameProps {
  variant?: DeviceVariant;
  /** Image source. Omit when passing custom children (e.g. a native illustrative UI). */
  src?: string;
  alt?: string;
  /** Fake address shown in the browser chrome. */
  url?: string;
  /** Aspect ratio for the screen area, e.g. "16 / 10". Prevents layout shift. */
  aspect?: string;
  /** Below-the-fold images should lazy-load. Hero/above-the-fold can pass "eager". */
  loading?: 'eager' | 'lazy';
  className?: string;
  children?: React.ReactNode;
  /** object-position for cropping full-page screenshots to their strongest region. */
  objectPosition?: string;
}
function Screen({
  src,
  alt,
  aspect,
  loading = 'lazy',
  objectPosition,
  children
}: Pick<DeviceFrameProps, 'src' | 'alt' | 'aspect' | 'loading' | 'objectPosition' | 'children'>) {
  const [errored, setErrored] = useState(false);
  const pendingFile = src ? src.split('/').pop() : undefined;
  return <div className="relative w-full overflow-hidden bg-white" style={src && aspect ? {
    aspectRatio: aspect
  } : undefined} data-id="element-632" data-mp="8aicby9hhamm">
      {src && !errored ? <img src={src} alt={alt ?? ''} loading={loading} decoding="async" onError={() => setErrored(true)} className="h-full w-full object-cover object-top" style={objectPosition ? {
      objectPosition
    } : undefined} data-id="element-633" data-mp="ca915wfg9goi" /> : src && errored ? <div className="flex h-full w-full flex-col items-center justify-center gap-2 border border-dashed border-gray-300 bg-paper-dark p-6 text-center" data-id="element-634" data-mp="r0q11bcfryja">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-text-muted" data-id="element-635" data-mp="vzjitfyrabp9">
            Real proof asset pending
          </p>
          <code className="rounded bg-white px-2 py-1 text-[0.7rem] text-navy ring-1 ring-gray-200" data-id="element-636" data-mp="m9zuhus9xpn0">
            public/proof/{pendingFile}
          </code>
        </div> : children}
    </div>;
}
export function DeviceFrame({
  variant = 'browser',
  src,
  alt,
  url = 'authorleverage.com',
  aspect = '16 / 10',
  loading = 'lazy',
  className = '',
  children,
  objectPosition
}: DeviceFrameProps) {
  if (variant === 'phone') {
    return <div className={`relative mx-auto w-full max-w-[300px] rounded-[2.5rem] border-[10px] border-navy bg-navy p-0 shadow-[0_40px_80px_-30px_rgba(26,40,56,0.55)] ${className}`} data-id="element-637" data-mp="1jo99bwxneeov">
        <div className="absolute left-1/2 top-2.5 z-10 h-1.5 w-16 -translate-x-1/2 rounded-full bg-white/25" data-id="element-638" data-mp="1alzzzae48rd2" />
        <div className="overflow-hidden rounded-[1.9rem]" data-id="element-639" data-mp="15oqc3z6dknrc">
          <Screen src={src} alt={alt} aspect={aspect ?? '9 / 19'} loading={loading} objectPosition={objectPosition} data-id="element-640" data-mp="10xi1x0x80yd4">
            {children}
          </Screen>
        </div>
      </div>;
  }
  if (variant === 'laptop') {
    return <div className={`mx-auto w-full ${className}`} data-id="element-641" data-mp="qztylmljuijc">
        <div className="rounded-t-2xl border-[3px] border-b-0 border-navy bg-navy p-2 shadow-[0_40px_80px_-30px_rgba(26,40,56,0.5)]" data-id="element-642" data-mp="qx7q9kqu3z02">
          <div className="overflow-hidden rounded-lg bg-white" data-id="element-643" data-mp="hy44ua4737ie">
            <Screen src={src} alt={alt} aspect={aspect} loading={loading} objectPosition={objectPosition} data-id="element-644" data-mp="i0qdye6qhv2q">
              {children}
            </Screen>
          </div>
        </div>
        <div className="relative mx-auto h-3 w-[112%] -translate-x-[5.4%] rounded-b-2xl bg-navy-light" data-id="element-645" data-mp="2yizi4noos2i3">
          <div className="absolute left-1/2 top-0 h-1.5 w-24 -translate-x-1/2 rounded-b-lg bg-navy" data-id="element-646" data-mp="32kb6kpvtqhop" />
        </div>
      </div>;
  }

  // browser
  return <div className={`overflow-hidden rounded-2xl border border-white/10 bg-white shadow-[0_40px_80px_-30px_rgba(26,40,56,0.55)] ring-1 ring-black/5 ${className}`} data-id="element-647" data-mp="2aw12ttcaz0lr">
      <div className="flex items-center gap-2 border-b border-gray-200/80 bg-paper-dark px-4 py-3" data-id="element-648" data-mp="2jvwhw98zbfr2">
        <span className="h-3 w-3 rounded-full bg-[#ff5f57]" data-id="element-649" data-mp="2ommlpi1rbajn" />
        <span className="h-3 w-3 rounded-full bg-[#febc2e]" data-id="element-650" data-mp="2oo7fh5g8ypgg" />
        <span className="h-3 w-3 rounded-full bg-[#28c840]" data-id="element-651" data-mp="1r7s759bjwchi" />
        <div className="ml-3 hidden flex-1 items-center rounded-md bg-white px-3 py-1 text-xs font-medium text-text-muted sm:flex" data-id="element-652" data-mp="1r5egrdrtfoga">
          {url}
        </div>
      </div>
      <Screen src={src} alt={alt} aspect={aspect} loading={loading} objectPosition={objectPosition} data-id="element-653" data-mp="2o4ix8usen9y">
        {children}
      </Screen>
    </div>;
}